import { uuidv4 } from '@firebase/util';
import React, { useState } from 'react';

import { LinkAttachement, PartialFileAttachement, PartialLinkAttachement } from '../../../pages/mindmap/types';
import { useMindmap } from '../../../pages/mindmap/use-mindmap';
import { PanelMenu, PanelMenuValue } from '../../ui/panel-menu';
import { useUnmount } from './../../../utils/useUnmount';
import { LinkAttachementChip } from './link-attachement-chip';

interface LinkContextMenuProps {
  attachement?: LinkAttachement;
  nodeId?: string;
  creatingMode?: boolean;
  onCreateClick?: (attachement: PartialFileAttachement | undefined) => void;
}

export const LinkContextMenu = ({ attachement, nodeId, creatingMode, onCreateClick }: LinkContextMenuProps) => {
  const {
    manipulation: {
      updateAttachement,
      deleteAttachement,
      setShadowNodeAttachementsWidth,
      calculateMinAttachementDimensions,
    },
    contextMenu: { setActiveMenu },
    data: { nodeMap },
  } = useMindmap();

  const [attachementChanges, setAttachementChanges] = useState<PartialLinkAttachement | undefined>();

  const onDeleteClick = () => {
    if (nodeId && attachement) {
      deleteAttachement(nodeId, attachement.id);
      setActiveMenu(undefined);
    }
  };

  const onChange = () => {
    if (nodeId) {
      // update shadow text element width because text breaks can change if width changes
      const node = nodeMap[nodeId];
      if (node) {
        const newNode = {
          ...node,
          attachements: creatingMode
            ? [...(node.attachements || []), createUpdatedAttachement()]
            : node.attachements?.map((a) => (a.id == attachement?.id ? { ...a, ...createUpdatedAttachement() } : a)),
        };
        const { attachementMinWidth } = calculateMinAttachementDimensions(newNode);
        setShadowNodeAttachementsWidth(attachementMinWidth);
      }
    }
  };

  const onChangeTitle: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    setAttachementChanges({
      ...attachement,
      ...attachementChanges,
      name: e.target.value,
    });
    onChange();
  };

  const onChangeLink: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const newAttachementChanges = {
      ...attachement,
      ...attachementChanges,
      url: e.target.value,
    };
    setAttachementChanges(newAttachementChanges);
    onChange();
  };

  const createUpdatedAttachement = (): LinkAttachement => {
    const tempChipRenderer = document.getElementById(`temp-link-chip-renderer-${nodeId}`);
    const width = tempChipRenderer
      ? parseFloat(parseFloat(window.getComputedStyle(tempChipRenderer).width).toPrecision(12))
      : 0;

    return {
      type: 'link',
      id: uuidv4(),
      name: '',
      url: '',
      ...attachement,
      ...attachementChanges,
      width: width || attachement?.width || 0,
    };
  };

  const applyAttachementChanges = () => {
    if (attachement && nodeId) {
      updateAttachement(nodeId, createUpdatedAttachement());
      setAttachementChanges(undefined);
    }
  };

  useUnmount(() => {
    if (attachementChanges) applyAttachementChanges();
  });

  const panelMenu: PanelMenuValue = [
    {
      label: 'Titel',
      icon: 'pi pi-fw ri-palette-line',
      type: 'input-text',
      value: attachementChanges?.name || attachement?.name,
      onChange: onChangeTitle,
      autoselect: true,
    },
    {
      label: 'Link',
      icon: 'pi pi-fw ri-palette-line',
      type: 'input-text',
      value: attachementChanges?.url || attachement?.url,
      onChange: onChangeLink,
    },
    {
      separator: true,
    },
    ...(creatingMode
      ? [
          {
            label: 'Speichern',
            icon: 'pi pi-fw pi-save',
            onClick: () => (onCreateClick ? onCreateClick(createUpdatedAttachement()) : () => {}),
          },
        ]
      : [
          {
            label: 'Löschen',
            icon: 'pi pi-fw ri-delete-bin-2-line',
            onClick: onDeleteClick,
          },
        ]),
  ];

  return (
    <>
      <PanelMenu value={panelMenu} />
      <div style={{ position: 'absolute', width: '1000vw', visibility: 'hidden' }}>
        <LinkAttachementChip
          id={`temp-link-chip-renderer-${nodeId}`}
          attachement={{ type: 'link', id: '', name: '', url: '', width: 0, ...attachement, ...attachementChanges }}
          selected={false}
        />
      </div>
    </>
  );
};
